import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BaseApiService } from '../baseApiService';
import { AuthenticationService } from '../../auth/authentication-service';
import { CreateAssetResponse } from './createAssetResponse';
import { CreateAssetCommand } from './createAssetCommand';
import { EditAssetCommand } from './editAssetCommand';
import { Asset } from './asset';
import { AssetSearchResult } from './search/assetSearchResult';
import { TechnicalDetails } from './technical-details/technical-details';
import { AssetsImportResponse } from './assetsImportResponse';
import { AssetSearchCriteria } from './search/assetSearchCriteria';
import { Const } from 'src/app/const';
import { Filter, SearchQuery } from './search/searchQuery';
import { EditTechnicalDetails } from './technical-details/edit-technical-details';

@Injectable({
  providedIn: 'root'
})
export class AssetApiService extends BaseApiService {
  constructor(http: HttpClient, authService: AuthenticationService) {
    super(http, authService);
  }

  createAsset(createAssetCommand: CreateAssetCommand): Observable<CreateAssetResponse> {
    return this.http.post<CreateAssetResponse>(`${this.apiUrl}/api/v4/assets/`, createAssetCommand);
  }

  updateAsset(id: string, updateAssetCommand: EditAssetCommand): Observable<Asset> {
    return this.http.put<Asset>(`${this.apiUrl}/api/v4/assets/${id}`, updateAssetCommand);
  }

  getAsset(assetid: string): Observable<Asset[]> {
    if (!assetid.trim()) {
      return of();
    }

    return this.http.get<Asset[]>(`${this.apiUrl}/api/v4/assets/${assetid}`).pipe(
      catchError(this.handleError<Asset[]>('getAsset', undefined))
    );
  }

  getAssetTechnicalDetails(id: string): Observable<TechnicalDetails> {
    return this.http.get<TechnicalDetails>(`${this.apiUrl}/api/v4/assets/${id}/technical-details`).pipe(
      catchError(this.handleError<TechnicalDetails>('getAssetTechnicalDetails', undefined))
    );
  }

  updateAssetTechnicalDetails(data: EditTechnicalDetails): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/api/v4/assets/${data.id}/technical-details`, data).pipe(
      catchError(this.handleError<void>('updateAssetTechnicalDetails', undefined))
    );
  }

  searchAsset(criteria: AssetSearchCriteria, start: number = 0): Observable<AssetSearchResult[]> {
    return this.http.post<AssetSearchResult[]>(`${this.apiUrl}/api/v2/search/assets?start=${start}&limit=${Const.SEARCH_QUERY_LIMIT}`, criteria.toSearchQuery()).pipe(
      catchError(this.handleError<AssetSearchResult[]>('searchAsset', undefined))
    );
  }

  searchAssetsForLocation(siteId: number, locationName: string | null): Observable<AssetSearchResult[]> {
    const siteIdFilter = new Filter("registration.site.id", "equals", [siteId.toString()]);
    const locatioNameFilter = new Filter("registration.location.name", "equals", [locationName]);
    const assetQuery = new SearchQuery([siteIdFilter, locatioNameFilter]);

    return this.http.post<AssetSearchResult[]>(`${this.apiUrl}/search/assets?limit=${Const.SEARCH_QUERY_LIMIT}`, assetQuery).pipe(
      catchError(this.handleError<AssetSearchResult[]>('searchAsset', undefined))
    );
  }

  searchAssetsForOfferingTree(siteId: number, offeringTreeCids: string[]): Observable<AssetSearchResult[]> {
    const siteIdFilter = new Filter("registration.site.id", "equals", [siteId.toString()]);
    const parentCidsFilter = new Filter("asset.parentCid", "equals", offeringTreeCids);
    const assetQuery = new SearchQuery([siteIdFilter, parentCidsFilter]);

    return this.http.post<AssetSearchResult[]>(`${this.apiUrl}/api/v2/search/assets?limit=${Const.SEARCH_QUERY_LIMIT}`, assetQuery).pipe(
      catchError(this.handleError<AssetSearchResult[]>('searchAsset', undefined))
    );
  }

  searchAssetsForSiteWithoutCid(siteId: number): Observable<AssetSearchResult[]> {
    const siteIdFilter = new Filter("registration.site.id", "equals", [siteId.toString()]);
    const locatioNameFilter = new Filter("asset.cid", "equals", [null]);
    const assetQuery = new SearchQuery([siteIdFilter, locatioNameFilter]);

    return this.http.post<AssetSearchResult[]>(`${this.apiUrl}/api/v2/search/assets?limit=${Const.SEARCH_QUERY_LIMIT}`, assetQuery).pipe(
      catchError(this.handleError<AssetSearchResult[]>('searchAsset', undefined))
    );
  }

  importAssets(formData: FormData): Observable<AssetsImportResponse> {
    return this.http.post<AssetsImportResponse>(`${this.apiUrl}/api/v4/assets/import`, formData);
  }
}
